import React, { useState, useEffect, useMemo } from "react";
import {
  Eye,
  Layers,
  X,
  Calendar,
  BadgeCheck,
  Search,
  ChevronDown,
  MapPin,
  Sparkles,
  Palette,
  Users,
  Heart,
  ImageIcon,
} from "lucide-react";
import axiosInstance from "../../services/api/axios";
import { toast } from "react-toastify";
import LoadingImage from "../../components/LoadingImage";
import PaginationControls from "../../components/PaginationControls";
import { getMediaUrl } from "../../utils/media";
import { getPresignedDownloadUrls } from "../../utils/fileUtils";
import PortfolioViewModal from "../../components/PortfolioViewModal";
import AvailabilityTag from "../../components/AvailabilityTag";

const designThemes = {
  1: "Modern",
  2: "Minimalistic",
  3: "Traditional",
  4: "Contemporary",
};

/** Browse API may nest portfolio as { portfolioItem: inner } or put fields directly on portfolioItem. */
function unwrapBrowsePortfolioRow(row) {
  if (!row) return null;
  return row.portfolioItem ?? row;
}

/** Featured iteration may come as { designIteration: {...} } or flattened on the same object. */
function getIterationPayload(featuredIteration) {
  if (!featuredIteration) return null;
  return featuredIteration.designIteration ?? featuredIteration;
}

/** Browse feed may use `featuredDesignIteration`; UI expects `featuredIteration`. Flat interaction rows have no itemType. */
function normalizeBrowseFeedItem(item) {
  if (!item || typeof item !== "object") return item;
  const featured = item.featuredIteration ?? item.featuredDesignIteration;
  const typeRaw = (item.itemType ?? "").toString().toUpperCase().replace(/-/g, "_");
  if (typeRaw === "FEATURED_ITERATION" && featured) {
    return { ...item, itemType: "FEATURED_ITERATION", featuredIteration: featured };
  }
  if (!item.itemType && featured) {
    return {
      ...item,
      itemType: "FEATURED_ITERATION",
      featuredIteration: featured,
    };
  }
  return item;
}

const PAGE_SIZE = 12;

const BrowsePortfolios = ({ buyerId, saleData }) => {
  const [browseItems, setBrowseItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageUrls, setImageUrls] = useState({});
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [selectedDesigner, setSelectedDesigner] = useState(null);
  const [selectedFeaturedIteration, setSelectedFeaturedIteration] = useState(null);
  const [shortlistedKeys, setShortlistedKeys] = useState(() => new Set());

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTheme, setSelectedTheme] = useState("all");
  const [selectedDesignerFilter, setSelectedDesignerFilter] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const propertyId = saleData?.propertyData?.propertyId || saleData?.propertyData?.id || null;
  // BV users hitting the interact/uninteract endpoints must send userId so
  // the BE can act on this buyer's behalf (and so RBAC + sale-link checks
  // pass). For buyer-on-buyer this would be ignored; harmless to send.
  const buyerUserId = saleData?.user?.userId || saleData?.user?.id || null;
  const buyerName = `${saleData?.user?.firstName || ""} ${saleData?.user?.lastName || ""}`.trim();
  const property = saleData?.propertyData || {};
  const project = property?.projectPhase?.project || {};
  const address = project?.address || {};
  const unitNo = property.propertyNo || "";
  const addressText = address.city && address.state
    ? `${project.projectName || ""}, ${address.city}, ${address.state}`
    : project.projectName || "";

  // Fetch browse items
  useEffect(() => {
    const fetchBrowseItems = async () => {
      if (!propertyId) return;
      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.append("propertyId", propertyId);
        params.append("limit", "50");

        const response = await axiosInstance.get(
          `/interior-designers/portfolio-items/browse?${params.toString()}`
        );

        const raw = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.items)
              ? response.items
              : [];
        setBrowseItems(raw.map(normalizeBrowseFeedItem));
      } catch (error) {
        console.error("Error fetching browse items:", error);
        toast.error("Failed to fetch designs");
      } finally {
        setLoading(false);
      }
    };

    fetchBrowseItems();
  }, [propertyId]);

  // Fetch presigned URLs for portfolio + featured iteration S3 keys
  useEffect(() => {
    const fetchImageUrls = async () => {
      if (browseItems.length === 0) return;

      const keySet = new Set();
      const addKey = (raw) => {
        const k = typeof raw === "string" ? raw.trim() : raw?.s3_key || raw?.s3Key || raw?.key;
        if (!k || typeof k !== "string") return;
        keySet.add(k);
      };

      browseItems.forEach((item) => {
        if (item.itemType === "PORTFOLIO_ITEM") {
          const portfolio = unwrapBrowsePortfolioRow(item.portfolioItem);
          const images = portfolio?.images;
          if (!Array.isArray(images)) return;
          images.forEach((img) => {
            if (typeof img === "string") {
              const s = img.trim();
              if (s && !s.startsWith("data:") && !s.startsWith("/9j/") && !s.startsWith("iVBORw0KGgo")) {
                addKey(s);
              }
              return;
            }
            if (
              img &&
              typeof img === "object" &&
              (img.s3_key || img.s3Key || img.key) &&
              !img.url &&
              !img.downloadUrl
            ) {
              addKey(img);
            }
          });
        }
        if (item.itemType === "FEATURED_ITERATION" && (item.featuredIteration || item.featuredDesignIteration)) {
          const iteration = getIterationPayload(item.featuredIteration ?? item.featuredDesignIteration);
          const media = iteration?.media;
          if (Array.isArray(media)) {
            media.forEach((m) => {
              if (m && typeof m === "object" && (m.s3_key || m.s3Key || m.key) && !m.url && !m.downloadUrl) addKey(m);
            });
          } else if (typeof media === "object" && media !== null) {
            const key = media.s3_key || media.s3Key || media.key;
            if (key && !media.url && !media.downloadUrl) addKey(media);
          }
        }
      });

      const uniqueKeys = [...keySet];
      if (uniqueKeys.length === 0) return;

      try {
        const presignedUrls = await getPresignedDownloadUrls(uniqueKeys, axiosInstance);
        const urlMap = {};
        presignedUrls.forEach((presigned, index) => {
          const downloadUrl = presigned.download_url || presigned.downloadUrl || presigned.url;
          if (!downloadUrl) return;
          const s3Key =
            presigned.s3_key || presigned.s3Key || presigned.key || uniqueKeys[index];
          if (s3Key) urlMap[s3Key] = downloadUrl;
        });
        setImageUrls((prev) => ({ ...prev, ...urlMap }));
      } catch (error) {
        console.error("Error fetching presigned URLs:", error);
      }
    };

    if (!loading && browseItems.length > 0) {
      fetchImageUrls();
    }
  }, [browseItems, loading]);

  const getImageSrc = (image) => {
    if (!image) return null;
    const s3Key = image?.s3_key || image?.s3Key || image?.key;
    if (typeof image === "object" && s3Key) {
      if (imageUrls[s3Key]) return imageUrls[s3Key];
      if (image.url || image.downloadUrl) return getMediaUrl(image) || image.url || image.downloadUrl;
      return null;
    }
    if (typeof image === "object" && image.url) return getMediaUrl(image);
    if (typeof image === "object" && image.base64) {
      const b = image.base64;
      if (b.startsWith("data:")) return b;
      if (b.startsWith("/9j/")) return `data:image/jpeg;base64,${b}`;
      if (b.startsWith("iVBORw0KGgo")) return `data:image/png;base64,${b}`;
      return `data:image/jpeg;base64,${b}`;
    }
    if (typeof image === "string") {
      if (imageUrls[image]) return imageUrls[image];
      if (image.startsWith("data:image/") || image.startsWith("/9j/") || image.startsWith("iVBORw0KGgo")) {
        if (image.startsWith("/9j/")) return `data:image/jpeg;base64,${image}`;
        if (image.startsWith("data:")) return image;
        if (image.startsWith("iVBORw0KGgo")) return `data:image/png;base64,${image}`;
        return `data:image/jpeg;base64,${image}`;
      }
      return null;
    }
    return null;
  };

  const getFeaturedIterationImage = (media) => {
    if (!media) return null;
    if (Array.isArray(media) && media.length > 0) return getImageSrc(media[0]);
    if (typeof media === "object" && media !== null) return getImageSrc(media);
    return null;
  };

  const formatPriceRange = (cost) => {
    if (!cost) return "Price on request";
    const val = parseFloat(cost);
    if (isNaN(val)) return "Price on request";
    if (val < 100000) return `₹${(val / 1000).toFixed(0)}k`;
    return `₹${(val / 100000).toFixed(1)}L`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const dt = new Date(dateStr);
    if (Number.isNaN(dt.getTime())) return String(dateStr);
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  // Flatten browse feed into card list { kind, designer, data, key, themeIds }
  const flatCards = useMemo(() => {
    const cards = [];
    browseItems.forEach((item, idx) => {
      if (item.itemType === "PORTFOLIO_ITEM") {
        const portfolio = unwrapBrowsePortfolioRow(item.portfolioItem);
        if (!portfolio) return;
        const designer = portfolio?.interiorDesigner ?? item.portfolioItem?.interiorDesigner ?? null;
        const designerId =
          designer?.interiorDesignerId ?? portfolio?.interiorDesignerId ?? `unknown-${idx}`;
        const themeIds = Array.isArray(portfolio.designThemeIds) ? portfolio.designThemeIds : [];
        cards.push({
          kind: "PORTFOLIO_ITEM",
          designer,
          designerId,
          data: portfolio,
          rawItem: item,
          themeIds,
          isAvailableOnSite: item.isAvailableOnSite ?? false,
          key: `p-${portfolio.interiorDesignerPortfolioItemId ?? portfolio.id ?? idx}`,
          shortlistKey: `portfolio:${portfolio.interiorDesignerPortfolioItemId ?? portfolio.id ?? idx}`,
        });
      } else if (item.itemType === "FEATURED_ITERATION") {
        const featured = item.featuredIteration ?? item.featuredDesignIteration;
        const iteration = getIterationPayload(featured);
        if (!iteration) return;
        const designerId =
          featured?.interiorDesignerId ?? featured?.interior_designer_id ?? `unknown-${idx}`;
        // Featured rows don't carry full designer object; preserve null
        const designer = item?.interiorDesigner ?? null;
        const featuredId =
          featured.featuredDesignIterationId ??
          featured.featured_design_iteration_id ??
          iteration.designIterationId ??
          iteration.design_iteration_id ??
          idx;
        cards.push({
          kind: "FEATURED_ITERATION",
          designer,
          designerId,
          data: featured,
          iteration,
          rawItem: item,
          themeIds: [],
          isAvailableOnSite: item.isAvailableOnSite ?? false,
          key: `f-${featuredId}`,
          shortlistKey: `featured:${featuredId}`,
        });
      }
    });
    return cards;
  }, [browseItems]);

  // Designer & theme dropdown options
  const themeOptions = useMemo(() => {
    const set = new Set();
    flatCards.forEach((c) => c.themeIds.forEach((id) => set.add(id)));
    return Array.from(set);
  }, [flatCards]);

  const designerOptions = useMemo(() => {
    const map = new Map();
    flatCards.forEach((c) => {
      const id = c.designerId;
      const name = c.designer?.name || `Designer #${id}`;
      if (!map.has(id)) map.set(id, name);
    });
    return Array.from(map.entries());
  }, [flatCards]);

  // Apply filters
  const filteredCards = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return flatCards.filter((c) => {
      if (selectedType !== "all" && c.kind !== selectedType) return false;
      if (selectedDesignerFilter !== "all" && String(c.designerId) !== String(selectedDesignerFilter)) return false;
      if (selectedTheme !== "all" && !c.themeIds.includes(Number(selectedTheme))) return false;
      if (q) {
        const title =
          c.kind === "PORTFOLIO_ITEM"
            ? (c.data.title || "")
            : (c.iteration?.interactionName ||
                c.iteration?.interaction_name ||
                c.iteration?.iterationName ||
                c.iteration?.iteration_name ||
                c.iteration?.title ||
                "");
        const description =
          c.kind === "PORTFOLIO_ITEM"
            ? (c.data.description || "")
            : (c.iteration?.designerNotes || "");
        const designerName = c.designer?.name || "";
        const haystack = `${title} ${description} ${designerName}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [flatCards, searchQuery, selectedTheme, selectedDesignerFilter, selectedType]);

  // KPI counts
  const kpiCounts = useMemo(() => {
    const themeSet = new Set();
    const designerSet = new Set();
    flatCards.forEach((c) => {
      c.themeIds.forEach((id) => themeSet.add(id));
      designerSet.add(c.designerId);
    });
    return {
      total: flatCards.length,
      themes: themeSet.size,
      designers: designerSet.size,
      shortlisted: shortlistedKeys.size,
    };
  }, [flatCards, shortlistedKeys]);

  // Pagination
  const totalFiltered = filteredCards.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageItems = filteredCards.slice(pageStart, pageStart + pageSize);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, selectedTheme, selectedDesignerFilter, selectedType, pageSize]);

  const hasActiveFilters =
    Boolean(searchQuery) ||
    selectedTheme !== "all" ||
    selectedDesignerFilter !== "all" ||
    selectedType !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedTheme("all");
    setSelectedDesignerFilter("all");
    setSelectedType("all");
  };

  const handleInteract = async (card) => {
    try {
      // Tack on userId when we know it — required for BV users (acting on
      // behalf of a buyer) and harmless for buyer-on-buyer (BE ignores it).
      const userIdParam = buyerUserId ? `&userId=${buyerUserId}` : "";
      if (card.kind === "PORTFOLIO_ITEM") {
        const portfolioItemId = card.data?.interiorDesignerPortfolioItemId;
        if (!portfolioItemId) throw new Error("Missing portfolio item id");
        await axiosInstance.post(
          `/interior-designers/portfolio-items/${portfolioItemId}/interact?propertyId=${propertyId}${userIdParam}`
        );
      } else {
        const feat = card.data;
        const featuredId =
          feat?.featuredDesignIterationId ?? feat?.featured_design_iteration_id;
        await axiosInstance.post(
          `/interior-designers/featured-iterations/${featuredId}/interact?propertyId=${propertyId}${userIdParam}`
        );
      }
      setShortlistedKeys((prev) => {
        const next = new Set(prev);
        next.add(card.shortlistKey);
        return next;
      });
      toast.success("Added to shortlist");
    } catch (error) {
      if (error.response?.status === 409) {
        setShortlistedKeys((prev) => {
          const next = new Set(prev);
          next.add(card.shortlistKey);
          return next;
        });
        toast.info("Already in shortlist");
      } else {
        toast.error("Failed to add to shortlist");
      }
    }
  };

  const handleViewPortfolio = (portfolio, designer) => {
    setSelectedPortfolio(portfolio);
    setSelectedDesigner(designer);
  };

  const handleCloseModal = () => {
    setSelectedPortfolio(null);
    setSelectedDesigner(null);
  };

  const handleViewFeatured = (featured, designer) => {
    setSelectedFeaturedIteration({ featured, designer });
  };

  return (
    <div className="space-y-5">
      {/* Slate gradient hero band */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-600 shadow-sm">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)",
          }}
        />
        <div className="relative px-6 py-6 sm:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
                <Sparkles size={12} />
                Buildvizer User · Browse designs
              </div>
              <h2 className="mt-3 text-2xl sm:text-3xl font-semibold text-white">Browse designer portfolios</h2>
              <p className="mt-1 text-sm text-white/70 truncate">
                {buyerName ? `${buyerName}` : "Buyer"}
                {unitNo ? ` · Unit ${unitNo}` : ""}
              </p>
              {addressText && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-white/60">
                  <MapPin size={12} />
                  <span className="truncate">{addressText}</span>
                </div>
              )}
            </div>
          </div>

          {/* KPI strip */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <Layers size={13} />
                Total designs
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpiCounts.total}</div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <Palette size={13} />
                Themes
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpiCounts.themes}</div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <Users size={13} />
                Designers
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpiCounts.designers}</div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-flamingo-200 text-xs">
                <Heart size={13} />
                Shortlisted so far
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpiCounts.shortlisted}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search by title, description, designer…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-10 pr-4 text-sm text-slate-800 placeholder-slate-400 outline-none transition-all focus:bg-white focus:border-flamingo-400 focus:ring-2 focus:ring-flamingo-200"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 lg:flex lg:items-center lg:gap-2">
            <div className="relative">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 min-w-[150px]"
              >
                <option value="all">All types</option>
                <option value="FEATURED_ITERATION">Featured designs</option>
                <option value="PORTFOLIO_ITEM">Portfolios</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
            </div>

            <div className="relative">
              <select
                value={selectedTheme}
                onChange={(e) => setSelectedTheme(e.target.value)}
                className="appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 min-w-[150px]"
              >
                <option value="all">All themes</option>
                {themeOptions.map((id) => (
                  <option key={id} value={id}>
                    {designThemes[id] || `Theme ${id}`}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
            </div>

            <div className="relative">
              <select
                value={selectedDesignerFilter}
                onChange={(e) => setSelectedDesignerFilter(e.target.value)}
                className="appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 min-w-[170px]"
              >
                <option value="all">All designers</option>
                {designerOptions.map(([id, name]) => (
                  <option key={String(id)} value={String(id)}>
                    {name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
            </div>
          </div>
        </div>

        {!loading && (
          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
            <p className="text-xs text-slate-600">
              Showing{" "}
              <span className="font-semibold text-slate-800">{totalFiltered}</span> of{" "}
              <span className="font-semibold text-slate-800">{flatCards.length}</span>{" "}
              {flatCards.length === 1 ? "design" : "designs"}
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs font-medium text-flamingo-600 hover:text-flamingo-700"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Loading / Empty / Grid */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-flamingo-200 border-t-flamingo-500" />
          <p className="mt-3 text-sm text-slate-500">Loading designs…</p>
        </div>
      ) : flatCards.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Palette className="text-slate-400" size={24} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">No designs available</p>
          <p className="mt-1 text-sm text-slate-500">
            There are no designer portfolios or featured designs for this property yet.
          </p>
        </div>
      ) : totalFiltered === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Search className="text-slate-400" size={24} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">No designs match your filters</p>
          <p className="mt-1 text-sm text-slate-500">Try clearing the search or adjusting your filters.</p>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="mt-4 text-sm font-medium text-flamingo-600 hover:text-flamingo-700"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {pageItems.map((card) => {
              const isShortlisted = shortlistedKeys.has(card.shortlistKey);
              const designerName = card.designer?.name || "Interior Designer";

              if (card.kind === "PORTFOLIO_ITEM") {
                const portfolio = card.data;
                const firstImage = portfolio.images?.[0] || null;
                const imageSrc = getImageSrc(firstImage);
                const firstS3Key =
                  typeof firstImage === "string"
                    ? firstImage.trim()
                    : firstImage?.s3_key || firstImage?.s3Key;
                const isImageLoading =
                  Boolean(firstS3Key) &&
                  !imageSrc &&
                  !(typeof firstImage === "object" && (firstImage?.url || firstImage?.downloadUrl)) &&
                  !(typeof firstImage === "string" && firstImage.startsWith("data:"));
                const themeNames = (portfolio.designThemeIds || [])
                  .map((id) => designThemes[id])
                  .filter(Boolean);
                const primaryTheme = themeNames[0];

                return (
                  <div
                    key={card.key}
                    className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                  >
                    {/* Image header */}
                    <div className="relative" onClick={() => handleViewPortfolio(portfolio, card.designer)}>
                      {imageSrc ? (
                        <LoadingImage
                          src={imageSrc}
                          alt={portfolio.title}
                          containerClassName="h-44 w-full"
                          className="h-44 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : isImageLoading ? (
                        <div className="flex h-44 w-full items-center justify-center bg-slate-100">
                          <div className="h-7 w-7 animate-spin rounded-full border-2 border-flamingo-200 border-t-flamingo-500" />
                        </div>
                      ) : (
                        <div className="flex h-44 w-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                          <ImageIcon className="h-10 w-10 text-slate-300" />
                        </div>
                      )}
                      {/* Top overlays */}
                      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-2.5">
                        {primaryTheme ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                            {primaryTheme}
                          </span>
                        ) : <span />}
                        {isShortlisted && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-flamingo-500 to-flamingo-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
                            <Heart size={11} className="fill-white" />
                            Shortlisted
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Body */}
                    <div className="flex flex-1 flex-col p-4">
                      <h3
                        className="truncate text-base font-semibold text-slate-900 transition-colors hover:text-flamingo-600"
                        title={portfolio.title}
                        onClick={() => handleViewPortfolio(portfolio, card.designer)}
                      >
                        {portfolio.title || "Untitled Portfolio"}
                      </h3>
                      <p className="mt-0.5 truncate text-xs font-medium text-slate-600" title={designerName}>
                        by {designerName}
                      </p>

                      {card.isAvailableOnSite && (
                        <div className="mt-2">
                          <AvailabilityTag isAvailableOnSite size="xs" hideWhenRemote />
                        </div>
                      )}

                      {portfolio.description && (
                        <p className="mt-2 line-clamp-2 text-xs text-slate-500">{portfolio.description}</p>
                      )}

                      <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center rounded-full bg-flamingo-50 px-2.5 py-0.5 text-[11px] font-medium text-flamingo-700">
                          {formatPriceRange(portfolio.overallCost)}
                        </span>
                        {themeNames.slice(1, 3).map((t, i) => (
                          <span key={i} className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700">
                            {t}
                          </span>
                        ))}
                      </div>

                      <div className="mt-auto pt-4">
                        <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
                          <button
                            type="button"
                            onClick={() => handleViewPortfolio(portfolio, card.designer)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-flamingo-600"
                          >
                            <Eye size={13} />
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => handleInteract(card)}
                            disabled={isShortlisted}
                            className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                              isShortlisted
                                ? "border border-flamingo-200 bg-flamingo-50 text-flamingo-700 cursor-not-allowed"
                                : "border border-flamingo-200 bg-flamingo-50 text-flamingo-700 hover:bg-flamingo-100"
                            }`}
                            title={isShortlisted ? "Already shortlisted" : "Shortlist for buyer"}
                          >
                            <Heart size={13} className={isShortlisted ? "fill-flamingo-500" : ""} />
                            {isShortlisted ? "Shortlisted" : "Shortlist"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              // FEATURED_ITERATION card
              const featured = card.data;
              const iteration = card.iteration;
              const iterationDisplayName =
                iteration.interactionName ||
                iteration.interaction_name ||
                iteration.iterationName ||
                iteration.iteration_name ||
                iteration.title ||
                (iteration.iterationNumber != null
                  ? `Design Iteration #${iteration.iterationNumber}`
                  : "Design Iteration");
              const iterationImage = getFeaturedIterationImage(iteration.media);
              const isFinal = Boolean(iteration.isFinal ?? iteration.is_final);
              const submittedOn = iteration.createdDate || iteration.created_date || featured.createdDate || featured.created_date;

              return (
                <div
                  key={card.key}
                  className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                >
                  <div className="relative" onClick={() => handleViewFeatured(featured, card.designer)}>
                    {iterationImage ? (
                      <LoadingImage
                        src={iterationImage}
                        alt={`Design iteration #${iteration.iterationNumber}`}
                        containerClassName="h-44 w-full"
                        className="h-44 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-44 w-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                        <ImageIcon className="h-10 w-10 text-slate-300" />
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-2.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-flamingo-600/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                        <Layers size={11} />
                        Real Project
                      </span>
                      {isShortlisted && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-flamingo-500 to-flamingo-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
                          <Heart size={11} className="fill-white" />
                          Shortlisted
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col p-4">
                    <h3
                      className="truncate text-base font-semibold text-slate-900 transition-colors hover:text-flamingo-600"
                      title={iterationDisplayName}
                      onClick={() => handleViewFeatured(featured, card.designer)}
                    >
                      {iterationDisplayName}
                    </h3>
                    <p className="mt-0.5 truncate text-xs font-medium text-slate-600" title={designerName}>
                      by {designerName}
                    </p>

                    {card.isAvailableOnSite && (
                      <div className="mt-2">
                        <AvailabilityTag isAvailableOnSite size="xs" hideWhenRemote />
                      </div>
                    )}

                    {iteration.designerNotes && (
                      <p className="mt-2 line-clamp-2 text-xs text-slate-500">{iteration.designerNotes}</p>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center rounded-full bg-flamingo-50 px-2.5 py-0.5 text-[11px] font-medium text-flamingo-700">
                        {formatPriceRange(iteration.estimatedPrice)}
                      </span>
                      {isFinal && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700">
                          <BadgeCheck size={11} />
                          Final
                        </span>
                      )}
                    </div>

                    {submittedOn && (
                      <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-400">
                        <Calendar size={11} />
                        <span>{formatDate(submittedOn)}</span>
                      </div>
                    )}

                    <div className="mt-auto pt-4">
                      <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
                        <button
                          type="button"
                          onClick={() => handleViewFeatured(featured, card.designer)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-flamingo-600"
                        >
                          <Eye size={13} />
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInteract(card)}
                          disabled={isShortlisted}
                          className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                            isShortlisted
                              ? "border border-flamingo-200 bg-flamingo-50 text-flamingo-700 cursor-not-allowed"
                              : "border border-flamingo-200 bg-flamingo-50 text-flamingo-700 hover:bg-flamingo-100"
                          }`}
                          title={isShortlisted ? "Already shortlisted" : "Shortlist for buyer"}
                        >
                          <Heart size={13} className={isShortlisted ? "fill-flamingo-500" : ""} />
                          {isShortlisted ? "Shortlisted" : "Shortlist"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Inline pagination — bare, variant="list" */}
          {totalFiltered > pageSize && (
            <PaginationControls
              pagination={{
                page: currentPage,
                pageSize,
                total: totalFiltered,
                totalPages,
                hasNext: currentPage < totalPages,
                hasPrevious: currentPage > 1,
              }}
              onPageChange={(p) => setPage(p)}
              onPageSizeChange={(s) => setPageSize(s)}
              itemName="designs"
              variant="list"
              showInfo={true}
            />
          )}
        </>
      )}

      {/* Portfolio View Modal */}
      <PortfolioViewModal
        isOpen={selectedPortfolio !== null}
        onClose={handleCloseModal}
        portfolio={selectedPortfolio}
        designer={selectedDesigner}
      />

      {/* Featured Iteration View Modal */}
      {selectedFeaturedIteration && (() => {
        const featured = selectedFeaturedIteration.featured;
        const iteration = getIterationPayload(featured);
        const imageSrc = iteration ? getFeaturedIterationImage(iteration.media) : null;
        if (!iteration) return null;
        return (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => setSelectedFeaturedIteration(null)}
          >
            <div
              className="w-full max-w-3xl bg-white rounded-2xl shadow-xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {iteration.interactionName ||
                      iteration.interaction_name ||
                      iteration.iterationName ||
                      iteration.iteration_name ||
                      iteration.title ||
                      (iteration.iterationNumber != null
                        ? `Featured Design Iteration #${iteration.iterationNumber}`
                        : "Featured Design Iteration")}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {selectedFeaturedIteration.designer?.name || "Interior Designer"}
                  </p>
                </div>
                <button
                  type="button"
                  className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
                  onClick={() => setSelectedFeaturedIteration(null)}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
                {imageSrc ? (
                  <div className="aspect-video bg-slate-100 rounded-xl overflow-hidden border border-slate-200">
                    <LoadingImage src={imageSrc} alt={`Iteration ${iteration.iterationNumber}`} containerClassName="w-full h-full" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="aspect-video bg-slate-100 rounded-xl flex items-center justify-center text-slate-400 border border-slate-200">
                    No Image
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs text-slate-500">Estimated Price</p>
                    <p className="text-base font-semibold text-slate-900">{formatPriceRange(iteration.estimatedPrice)}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs text-slate-500">Status</p>
                    <p className="text-base font-semibold text-slate-900">
                      {iteration.iterationStatus || iteration.iteration_status || "N/A"}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs text-slate-500">Final Iteration</p>
                    <p className="text-base font-semibold text-slate-900">
                      {Boolean(iteration.isFinal ?? iteration.is_final) ? "Yes" : "No"}
                    </p>
                  </div>
                </div>
                {iteration.designerNotes && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Designer Notes</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{iteration.designerNotes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default BrowsePortfolios;
